import { normalizeMedium } from "./normalizeMedium";
import { getArtTypeCanonicalKey } from "./artTypeTaxonomy";

const clean = (v) => (v == null ? "" : String(v).trim());

// artworks.currency n'accepte que ces codes ISO (contrainte artworks_currency_chk) ;
// l'OCR renvoie parfois un symbole ou un mot ("€", "Euros") qui violerait la contrainte.
const CURRENCY_ALIASES = {
  "€": "EUR", EUR: "EUR", EURO: "EUR", EUROS: "EUR",
  "$": "USD", USD: "USD", DOLLAR: "USD", DOLLARS: "USD",
  "£": "GBP", GBP: "GBP", LIVRE: "GBP", LIVRES: "GBP",
  CHF: "CHF", FRANC: "CHF", FRANCS: "CHF",
  "¥": "JPY", JPY: "JPY", YEN: "JPY",
  CNY: "CNY", RMB: "CNY", YUAN: "CNY",
  HKD: "HKD",
};
const normalizeCurrency = (value) => {
  const raw = clean(value).toUpperCase();
  if (!raw) return null;
  return CURRENCY_ALIASES[raw] || null;
};

const toNumber = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const normalizeDate = (value) => {
  const raw = clean(value);
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);

  const fr = raw.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (fr) {
    const [, d, m, y] = fr;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  return raw;
};

const normalizeComparableText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’']/g, "'")
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Source de vérité unique : la taxonomie centralisée (src/utils/artTypeTaxonomy.js).
// Mode strict → renvoie null si rien ne correspond (ne pas écraser en « Autre »
// une valeur OCR/IA non reconnue).
const normalizeTypeOfArt = (value) => getArtTypeCanonicalKey(value, { strict: true });

const normalizeAcquisitionType = (value) => {
  const raw = normalizeComparableText(value);
  if (!raw) return null;

  const map = {
    auction: ["auction", "maison de vente", "encheres", "enchères", "bordereau", "adjudication"],
    artist: ["artist", "artiste", "vente artiste", "atelier", "studio"],
    gallery: ["gallery", "galerie", "dealer", "marchand"],
    private: ["private", "vente privee", "vente privée", "particulier"],
    donation: ["donation", "don", "gift", "schenkung"],
    exchange: ["exchange", "echange", "échange", "swap"],
    inheritance: ["inheritance", "heritage", "héritage", "succession", "legs"],
    purchase: ["purchase", "achat", "acquisition"],
    other: ["other", "autre"],
  };

  for (const [key, terms] of Object.entries(map)) {
    if (terms.some((term) => raw.includes(normalizeComparableText(term)))) {
      return key;
    }
  }

  return null;
};

const parseDimensions = (value) => {
  const raw = clean(value);
  const m = raw.match(/(\d+[.,]?\d*)\s*[x×]\s*(\d+[.,]?\d*)(?:\s*[x×]\s*(\d+[.,]?\d*))?/i);
  if (!m) return null;

  const h = Number(m[1].replace(",", "."));
  const w = Number(m[2].replace(",", "."));
  const d = m[3] ? Number(m[3].replace(",", ".")) : null;

  if (!Number.isFinite(h) || !Number.isFinite(w)) return null;

  return { h, w, d, unit: "cm" };
};

export function mapOcrToArtworkPatch({ extracted = {}, artwork = {} }) {
  const patch = {};
  const details = artwork.acquisitionDetails || {};

  if (extracted.title) patch.title = clean(extracted.title);
  if (extracted.artist) patch.artist = clean(extracted.artist);
  if (extracted.year) patch.year = clean(extracted.year);

  const mediumRaw =
    extracted.medium ||
    extracted.medium_clean ||
    extracted.technique ||
    extracted.medium_original;

  if (mediumRaw) {
    const normalized = normalizeMedium(mediumRaw);

    patch.medium = normalized.medium || clean(mediumRaw);
    patch.medium_original = normalized.medium_original || clean(mediumRaw);

    if (normalized.medium_category) {
      patch.medium_category = normalized.medium_category;
    }
  }

  const categoryRaw =
    extracted.medium_category ||
    extracted.category ||
    extracted.object_type;

  if (categoryRaw && !patch.medium_category) {
    const normalized = normalizeMedium(categoryRaw);
    patch.medium_category = normalized.medium_category || normalizeComparableText(categoryRaw);
  }

  const typeOfArt =
    normalizeTypeOfArt(extracted.typeOfArt) ||
    normalizeTypeOfArt(extracted.type_of_art) ||
    normalizeTypeOfArt(extracted.period) ||
    normalizeTypeOfArt(extracted.market_segment);

  if (typeOfArt) patch.typeOfArt = typeOfArt;

  const dims = parseDimensions(extracted.dimensions);
  if (dims) {
    patch.dimensions = `${dims.h} x ${dims.w}${dims.d !== null ? ` x ${dims.d}` : ""} ${dims.unit}`;
    patch.dimensions_height = dims.h;
    patch.dimensions_width = dims.w;
    patch.height = dims.h;
    patch.width = dims.w;
    patch.dimensions_unit = dims.unit;
    patch.unit = dims.unit;

    patch.dimensions_depth = dims.d;
    patch.depth = dims.d;

    patch.specifications = {
      ...(artwork.specifications || {}),
      dimensions: {
        ...((artwork.specifications || {}).dimensions || {}),
        height: dims.h,
        width: dims.w,
        depth: dims.d ?? "",
        unit: dims.unit,
      },
    };
  }

  const acquisitionType = normalizeAcquisitionType(
    extracted.acquisition_type ||
    extracted.sale_type ||
    extracted.document_type
  );

  if (acquisitionType) {
    patch.acquisition_type = acquisitionType;
  }

  const acquisitionDate = normalizeDate(
    extracted.acquisition_date ||
    extracted.date ||
    extracted.invoice_date ||
    extracted.donation_date ||
    extracted.exchange_date ||
    extracted.auction_sale_date
  );

  if (acquisitionDate) {
    patch.acquisition_date = acquisitionDate;
  }

  const acquisitionPrice = toNumber(
    extracted.acquisition_price ||
    extracted.price ||
    extracted.total_amount ||
    extracted.declared_value ||
    extracted.donation_value ||
    extracted.exchange_value
  );

  if (acquisitionPrice !== null) {
    patch.acquisition_price = acquisitionPrice;
    patch.price = acquisitionPrice;
  }

  const currency = normalizeCurrency(extracted.currency);
  if (currency) {
    patch.currency = currency;
  }

  if (extracted.auction_house) {
    patch.auction_house = clean(extracted.auction_house);
    patch.acquisition_source = clean(extracted.auction_house);
    patch.acquisition_type = "auction";
  }

  if (extracted.auction_sale_name) patch.auction_sale_name = clean(extracted.auction_sale_name);
  if (extracted.auction_lot_number) patch.auction_lot_number = clean(extracted.auction_lot_number);
  if (extracted.auction_sale_date) patch.auction_sale_date = normalizeDate(extracted.auction_sale_date);

  const hammer = toNumber(extracted.auction_hammer_price);
  const fees = toNumber(extracted.auction_fees);

  if (hammer !== null) patch.auction_hammer_price = hammer;
  if (fees !== null) patch.auction_fees = fees;

  if ((hammer !== null || fees !== null) && acquisitionPrice === null) {
    patch.acquisition_price = (hammer || 0) + (fees || 0);
    patch.price = patch.acquisition_price;
  }

  const seller = clean(extracted.seller);
  const buyer = clean(extracted.buyer);
  const source =
    seller ||
    clean(extracted.donor) ||
    clean(extracted.exchange_counterparty) ||
    clean(extracted.source) ||
    clean(artwork.acquisition_source);

  if (source && patch.acquisition_type !== "auction") {
    patch.acquisition_source = source;
  }

  patch.acquisitionDetails = {
    ...details,

    ...(patch.acquisition_type ? {
      type: patch.acquisition_type,
      acquisition_type: patch.acquisition_type,
      sale_type: patch.acquisition_type,
    } : {}),

    ...(patch.acquisition_date ? {
      date: patch.acquisition_date,
      acquisition_date: patch.acquisition_date,
    } : {}),

    ...(patch.acquisition_source ? {
      source: patch.acquisition_source,
      acquisition_source: patch.acquisition_source,
    } : {}),

    ...(seller ? { seller } : {}),
    ...(buyer ? { buyer } : {}),

    ...(patch.acquisition_type === "donation" && source ? {
      donor: source,
      donation_source: source,
    } : {}),

    ...(patch.acquisition_type === "exchange" ? {
      exchange_counterparty: buyer || seller || source || "",
    } : {}),

    ...(acquisitionPrice !== null ? {
      price: acquisitionPrice,
      purchase_price: acquisitionPrice,
      purchasePrice: acquisitionPrice,
      declared_value: acquisitionPrice,
      donation_value: patch.acquisition_type === "donation" ? acquisitionPrice : details.donation_value,
      exchange_value: patch.acquisition_type === "exchange" ? acquisitionPrice : details.exchange_value,
    } : {}),

    ...(patch.auction_house ? { auction_house: patch.auction_house } : {}),
    ...(patch.auction_sale_name ? { auction_sale_name: patch.auction_sale_name } : {}),
    ...(patch.auction_sale_date ? { auction_sale_date: patch.auction_sale_date } : {}),
    ...(patch.auction_lot_number ? { lot_number: patch.auction_lot_number, auction_lot_number: patch.auction_lot_number } : {}),
    ...(hammer !== null ? { hammer_price: hammer, auction_hammer_price: hammer } : {}),
    ...(fees !== null ? { auction_fees: fees } : {}),
  };

  delete patch.seller;
  delete patch.buyer;

  return patch;
}